import { useEffect, useMemo, useState } from 'react';
import { PALETTE, minutesToHHMM } from '@micplot/shared';
import Avatar from '../components/Avatar';
import { usePresenters, useChannels, useAssignments } from '../state/store';

/**
 * Big, low-density board for a monitor someone reads from across the room:
 * who is live now, and who is walking up next.
 */
export default function StageManager() {
  const presenters = usePresenters();
  const channels = useChannels();
  const assignments = useAssignments();

  // Re-render on the minute so "now" stays true through a show.
  const [now, setNow] = useState(() => nowMinutes());
  useEffect(() => {
    const t = setInterval(() => setNow(nowMinutes()), 15_000);
    return () => clearInterval(t);
  }, []);

  const pById = useMemo(() => new Map(presenters.map(p => [p.id, p])), [presenters]);
  const cById = useMemo(() => new Map(channels.map(c => [c.id, c])), [channels]);

  const onStage = assignments
    .filter(a => !a.scheduled || (a.start <= now && now < a.end))
    .sort((a, b) => (cById.get(a.channelId)?.number ?? 0) - (cById.get(b.channelId)?.number ?? 0));

  const upNext = assignments
    .filter(a => a.scheduled && a.start > now)
    .sort((a, b) => a.start - b.start)
    .slice(0, 8);

  /*
   * Both columns key off the wall clock, so before the half and after the
   * curtain this view was two empty boxes -- on a show day that is most of the
   * day, and the plot it could have shown was sitting right there. When
   * nothing is running, show the plot instead and say plainly that is what
   * this is.
   */
  const showingPlot = onStage.length === 0 && upNext.length === 0
    && assignments.length > 0;
  const wholePlot = showingPlot
    ? assignments
      .slice()
      .sort((a, b) => (cById.get(a.channelId)?.number ?? 0)
        - (cById.get(b.channelId)?.number ?? 0))
    : [];

  const card = (
    a: typeof assignments[number],
    showStart: boolean,
    /* In the plot standing-in view nothing is running, so "until 19:45" reads
       as though it were -- the whole window is what you want there. */
    asPlot = false,
  ) => {
    const p = pById.get(a.presenterId);
    const c = cById.get(a.channelId);
    if (!p) return null;
    return (
      <div key={a.id} className="stage-card"
           style={{ borderLeftColor: PALETTE[p.color % PALETTE.length] }}>
        <Avatar presenter={p} size={56} />
        <div style={{ minWidth: 0 }}>
          <div className="stage-name">{p.name}</div>
          {p.characterName && <div className="stage-character">{p.characterName}</div>}
          <div className="stage-meta">
            {p.title ? `${p.title} · ` : ''}
            {p.micPreference}
            {!a.scheduled
              ? ' · whole show'
              : showStart
                ? ` · at ${minutesToHHMM(a.start)} (in ${a.start - now} min)`
                : asPlot
                  ? ` · ${minutesToHHMM(a.start)}–${minutesToHHMM(a.end)}`
                  : ` · until ${minutesToHHMM(a.end)}`}
          </div>
        </div>
        <span className="stage-tx">{c ? `TX${c.number}` : '—'}</span>
      </div>
    );
  };

  return (
    <div className="stage">
      <section className="panel stage-col">
        <div className="panel-head">
          <span className="dot on" />
          <span className="panel-title">On stage</span>
          <span className="count">{onStage.length}</span>
        </div>
        <div className="stage-list">
          {onStage.length > 0
            ? onStage.map(a => card(a, false))
            : showingPlot
              ? (
                <>
                  <div className="stage-note">
                    Nothing is running. This is the plot for the show.
                  </div>
                  {wholePlot.map(a => card(a, false, true))}
                </>
              )
              : <div className="empty">Nobody is on stage right now.</div>}
        </div>
      </section>

      <section className="panel stage-col">
        <div className="panel-head">
          <span className="dot" style={{ background: 'var(--warn)' }} />
          <span className="panel-title">Up next</span>
          <span className="count">{upNext.length}</span>
        </div>
        <div className="stage-list">
          {upNext.length === 0
            ? <div className="empty">Nothing else scheduled.</div>
            : upNext.map(a => card(a, true))}
        </div>
      </section>
    </div>
  );
}

const nowMinutes = () => new Date().getHours() * 60 + new Date().getMinutes();
